import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Plus, Search, Filter, Upload, LayoutGrid, List, User, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import api, { resolveFileUrl } from '../utils/api';

const Players = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [positionFilter, setPositionFilter] = useState(searchParams.get('position') || 'all');
  const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'list'

  const fetchPlayers = useCallback(async () => {
    try {
      const response = await api.get('/players');
      setPlayers(response.data);
    } catch (error) {
      toast.error('Failed to load players');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlayers();
  }, [fetchPlayers]);

  useEffect(() => {
    // Update position filter from URL params
    const urlPosition = searchParams.get('position');
    if (urlPosition && urlPosition !== positionFilter) {
      setPositionFilter(urlPosition);
    }
  }, [searchParams, positionFilter]);

  const [negotiationFilter, setNegotiationFilter] = useState('all');

  const filteredPlayers = useMemo(() => {
    let filtered = players;
    if (searchTerm) {
      filtered = filtered.filter(
        (p) =>
          p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          p.nationality.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (positionFilter !== 'all') {
      filtered = filtered.filter((p) => p.position === positionFilter || p.position2 === positionFilter);
    }
    if (negotiationFilter !== 'all') {
      if (negotiationFilter === 'any') {
        filtered = filtered.filter((p) => p.negotiation_status);
      } else {
        filtered = filtered.filter((p) => p.negotiation_status === negotiationFilter);
      }
    }
    return [...filtered].sort((a, b) => a.name.localeCompare(b.name));
  }, [players, searchTerm, positionFilter, negotiationFilter]);

  const handlePositionChange = (newPosition) => {
    setPositionFilter(newPosition);
    if (newPosition === 'all') {
      searchParams.delete('position');
    } else {
      searchParams.set('position', newPosition);
    }
    setSearchParams(searchParams);
  };

  const positions = ['GK', 'LB', 'LCB', 'CB', 'RCB', 'RB', 'DM', 'LCM', 'CM', 'RCM', 'AM', 'LW', 'RW', 'CF'];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <div className="relative bg-black/60 backdrop-blur-xl border-b border-white/5 overflow-hidden">
        <div className="hero-glow"></div>
        <div className="relative z-10 p-6 md:p-8 lg:p-12">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-5xl md:text-6xl font-bold uppercase tracking-tight leading-none text-white mb-2">
                Players
              </h1>
              <p className="text-lg text-zinc-300">{filteredPlayers.length} players in your roster</p>
            </div>
            <div className="flex gap-3">
              {/* View Toggle */}
              <div className="flex bg-zinc-900/50 border border-white/10 rounded-sm overflow-hidden">
                <button
                  onClick={() => setViewMode('cards')}
                  data-testid="view-mode-cards"
                  className={`px-4 py-2 text-sm font-bold uppercase tracking-wide transition-all flex items-center gap-2 ${
                    viewMode === 'cards' ? 'bg-white text-black' : 'text-white hover:bg-white/10'
                  }`}
                >
                  <LayoutGrid className="w-4 h-4" />
                  <span className="hidden sm:inline">Cards</span>
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  data-testid="view-mode-list"
                  className={`px-4 py-2 text-sm font-bold uppercase tracking-wide transition-all flex items-center gap-2 ${
                    viewMode === 'list' ? 'bg-white text-black' : 'text-white hover:bg-white/10'
                  }`}
                >
                  <List className="w-4 h-4" />
                  <span className="hidden sm:inline">List</span>
                </button>
              </div>
              <Link
                to="/players/bulk-import"
                data-testid="bulk-import-button"
                className="inline-flex items-center gap-2 bg-white/10 text-white hover:bg-white/20 border border-white/5 font-bold uppercase tracking-wide rounded-sm px-6 py-3 transition-all duration-200"
              >
                <Upload className="w-5 h-5" strokeWidth={2} />
                <span className="hidden sm:inline">Bulk Import</span>
              </Link>
              <Link
                to="/players/new"
                data-testid="add-new-player-button"
                className="inline-flex items-center gap-2 bg-white text-black hover:bg-zinc-200 font-bold uppercase tracking-wide rounded-sm px-6 py-3 transition-all duration-200"
              >
                <Plus className="w-5 h-5" strokeWidth={2} />
                Add Player
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6 md:p-8 lg:p-12">
        {/* Filters */}
        <div className="glass-card p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-zinc-500" strokeWidth={1.5} />
              <input
                type="text"
                placeholder="Search by name or nationality..."
                data-testid="search-players-input"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-zinc-900/50 border border-white/10 focus:border-white focus:ring-1 focus:ring-white rounded-md pl-12 pr-4 py-3 text-white placeholder:text-zinc-600 outline-none transition-all duration-200"
              />
            </div>
            <div className="relative">
              <Filter className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-zinc-500" strokeWidth={1.5} />
              <select
                value={positionFilter}
                onChange={(e) => handlePositionChange(e.target.value)}
                data-testid="filter-position-select"
                className="w-full bg-zinc-900/50 border border-white/10 focus:border-white focus:ring-1 focus:ring-white rounded-md pl-12 pr-4 py-3 text-white outline-none transition-all duration-200"
              >
                <option value="all">All Positions</option>
                {positions.map((pos) => (
                  <option key={pos} value={pos}>
                    {pos}
                  </option>
                ))}
              </select>
            </div>
            <div className="relative">
              <RefreshCw className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-zinc-500" strokeWidth={1.5} />
              <select
                value={negotiationFilter}
                onChange={(e) => setNegotiationFilter(e.target.value)}
                data-testid="filter-negotiation-select"
                className="w-full bg-zinc-900/50 border border-white/10 focus:border-white focus:ring-1 focus:ring-white rounded-md pl-12 pr-4 py-3 text-white outline-none transition-all duration-200"
              >
                <option value="all">All Negotiation Status</option>
                <option value="any">In Negotiation (Any)</option>
                <option value="start_conversation">Started</option>
                <option value="in_progress">In Progress</option>
                <option value="agreed">Agreed</option>
              </select>
            </div>
          </div>
        </div>

        {/* Players Display */}
        {filteredPlayers.length === 0 ? (
          <div className="glass-card p-12 text-center">
            <p className="text-zinc-500 text-lg">No players found. Add your first player to get started!</p>
          </div>
        ) : viewMode === 'cards' ? (
          /* Cards View */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPlayers.map((player) => (
              <Link
                key={player.id}
                to={`/players/${player.id}`}
                data-testid={`player-item-${player.id}`}
                className="glass-card p-6 card-hover group"
              >
                <div className="flex items-start gap-4 mb-4">
                  {/* Player Photo */}
                  {player.photo ? (
                    <img 
                      src={resolveFileUrl(player.photo)} 
                      alt={player.name}
                      className="w-16 h-16 rounded-lg object-cover border border-white/10 flex-shrink-0"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-lg bg-zinc-800 border border-white/10 flex items-center justify-center flex-shrink-0">
                      <User className="w-8 h-8 text-zinc-600" />
                    </div>
                  )}
                  
                  <div className="flex-1 min-w-0">
                    <h3 className="text-xl font-bold text-white mb-1 truncate">{player.name}</h3>
                    <p className="text-sm text-zinc-400">
                      {player.position}{player.position2 && `/${player.position2}`} • {player.nationality}
                      {player.preferred_foot && ` • ${player.preferred_foot}`}
                    </p>
                    {player.player_type && (
                      <span className="inline-block mt-1 px-2 py-0.5 bg-white/10 text-white text-xs font-medium rounded">
                        {player.player_type}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-between mb-4">
                  <div className="text-center">
                    <p className="text-xs uppercase tracking-widest text-zinc-500">Age</p>
                    <p className="text-2xl font-bold tabular-nums text-white">{player.age}</p>
                  </div>
                  {player.negotiation_status && (
                    <div className="text-center">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-bold uppercase border ${
                        player.negotiation_status === 'agreed' ? 'bg-green-500/20 text-green-400 border-green-500/30' :
                        player.negotiation_status === 'in_progress' ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' :
                        'bg-blue-500/20 text-blue-400 border-blue-500/30'
                      }`}>
                        <RefreshCw className="w-3 h-3" />
                        {player.negotiation_status === 'agreed' ? 'New Contract Agreed' :
                         player.negotiation_status === 'in_progress' ? 'Negotiating' :
                         'Negotiation Started'}
                      </span>
                    </div>
                  )}
                  {player.current_club && (
                    <div className="text-right">
                      <p className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Club</p>
                      <p className="text-sm font-medium text-white">{player.current_club}</p>
                    </div>
                  )}
                </div>

                {player.salary && (
                  <div className="pt-4 border-t border-white/5">
                    <p className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Salary</p>
                    <p className="text-lg font-bold tabular-nums text-green-400">
                      £{player.salary.toLocaleString()}/{player.salary_frequency || 'week'}
                    </p>
                  </div>
                )}
              </Link>
            ))}
          </div>
        ) : (
          /* List View */
          <div className="glass-card overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10 bg-black/30">
                  <th className="text-left p-4 text-xs font-bold uppercase tracking-widest text-zinc-500">Name</th>
                  <th className="text-left p-4 text-xs font-bold uppercase tracking-widest text-zinc-500">Position</th>
                  <th className="text-left p-4 text-xs font-bold uppercase tracking-widest text-zinc-500">Age</th>
                  <th className="text-left p-4 text-xs font-bold uppercase tracking-widest text-zinc-500">Nationality</th>
                  <th className="text-left p-4 text-xs font-bold uppercase tracking-widest text-zinc-500">Club</th>
                  <th className="text-left p-4 text-xs font-bold uppercase tracking-widest text-zinc-500">Type</th>
                  <th className="text-left p-4 text-xs font-bold uppercase tracking-widest text-zinc-500">Negotiation</th>
                  <th className="text-right p-4 text-xs font-bold uppercase tracking-widest text-zinc-500">Salary</th>
                </tr>
              </thead>
              <tbody>
                {filteredPlayers.map((player) => (
                  <tr
                    key={player.id}
                    className="border-b border-white/5 hover:bg-white/5 transition-colors cursor-pointer"
                    onClick={() => window.location.href = `/players/${player.id}`}
                    data-testid={`player-row-${player.id}`}
                  >
                    <td className="p-4">
                      <span className="font-bold text-white">{player.name}</span>
                      {player.preferred_foot && (
                        <span className="ml-2 text-xs text-zinc-500">({player.preferred_foot})</span>
                      )}
                    </td>
                    <td className="p-4">
                      <span className="px-2 py-1 bg-white/10 text-white text-xs font-bold rounded">
                        {player.position}{player.position2 && `/${player.position2}`}
                      </span>
                    </td>
                    <td className="p-4 text-white font-bold">{player.age}</td>
                    <td className="p-4 text-zinc-400">{player.nationality}</td>
                    <td className="p-4 text-zinc-400">{player.current_club || '-'}</td>
                    <td className="p-4">
                      {player.player_type ? (
                        <span className="text-xs text-zinc-400">{player.player_type}</span>
                      ) : '-'}
                    </td>
                    <td className="p-4">
                      {player.negotiation_status ? (
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${
                          player.negotiation_status === 'agreed' ? 'bg-green-500/20 text-green-400 border-green-500/30' :
                          player.negotiation_status === 'in_progress' ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' :
                          'bg-blue-500/20 text-blue-400 border-blue-500/30'
                        }`}>
                          {player.negotiation_status === 'agreed' ? 'Agreed' :
                           player.negotiation_status === 'in_progress' ? 'Negotiating' : 'Started'}
                        </span>
                      ) : '-'}
                    </td>
                    <td className="p-4 text-right">
                      {player.salary ? (
                        <span className="font-bold text-green-400">
                          £{player.salary.toLocaleString()}/{player.salary_frequency?.charAt(0) || 'w'}
                        </span>
                      ) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Players;
